/**
 * ⌘K search anywhere (screenshots/113, §2.20; §3.4 row 4). ARIA combobox + listbox: focus stays in the input,
 * ↑↓ move, ↵ opens (a moment opens the meeting at that second), ⌘↵ opens in a new tab, ⇧↵ follows, Tab /
 * Shift+Tab switch scope, Esc closes. Grouped Legislation · Meetings · People · Organizations & filings, followed
 * items first, 3 per group, then "Do something". Moments show the speaker's face and the snippet with the hit
 * highlighted; items already followed show "✓ Following".
 *
 * Opened by ⌘K / Ctrl+K, or the window event "palette:open" with detail {mode?: "search" | "follow", type?, q?, scope?}:
 * the Meetings / Legislation "+" opens it in follow mode (↵ follows the selected item), NoResults' "Search all
 * years" with scope "all"; scope "follows" searches only what you follow.
 */
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { ArrowRight, Building2, CalendarDays, Check, CornerDownLeft, FileText, Plus, ScrollText, Search, UserRound } from "lucide-react";
import { useFollows } from "../stores/followsStore";
import { Face } from "../components/shared/Face";
import { NoResults } from "../components/shared/States";
import { ListRow, RowLead, RowMain, RowSub, RowTitle, RowTrail } from "../components/shared/List";
import { useFocusTrap } from "../hooks/useFocusTrap";
import { duration, easing, prefersReducedMotion, reducedFallback } from "../motion";
import type { FollowType } from "../types";
import { actionsFor, groupResults, highlight, hrefOf, nextScope, opensAtMoment, paletteText, SCOPES, searchUrl,
  type PaletteItem, type Scope, type SearchResponse, type SearchResult, type SearchScope } from "./search";
import { followFromSearch, isSearchFollowed } from "./follow";
import { navigate } from "../app/router";

const TYPE_SCOPE: Partial<Record<FollowType, Scope>> = { legislation: "legislation", person: "person" };
const OWN_FOLLOW = new Set(["matter", "agenda", "person", "client"]);

function Hl({ text, q, hl }: { text: string; q: string; hl?: [number, number][] }) {
  return <>{highlight(text, q, hl).map((p, i) => (p.hit ? <mark key={i} className="hit">{p.text}</mark> : <Fragment key={i}>{p.text}</Fragment>))}</>;
}

function RowIcon({ r, data }: { r: PaletteItem; data: SearchResponse | null }) {
  if (r.kind === "action") return <span className="sp-icon" aria-hidden><Plus size={16} /></span>;
  const person = r.personId ? data?.people[r.personId] : undefined;
  if (person && (r.sub === "moment" || r.sub === "mention" || r.sub === "passage")) return <span className="sp-face"><Face name={person.name} photoUrl={person.photoUrl} size={32} /></span>;
  const I = r.kind === "legislation" ? ScrollText : r.kind === "meeting" ? CalendarDays : r.kind === "person" ? UserRound : r.kind === "organization" ? Building2 : FileText;
  return <span className="sp-icon" aria-hidden><I size={16} /></span>;
}

export function CommandPalette() {
  const follows = useFollows((s) => s.follows);
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<"search" | "follow">("search");
  const [onlyType, setOnlyType] = useState<FollowType | null>(null);
  const [q, setQ] = useState("");
  const [scope, setScope] = useState<Scope>("all");
  const [within, setWithin] = useState<SearchScope>("all");
  const [data, setData] = useState<SearchResponse | null>(null);
  const [active, setActive] = useState(0);
  const [, bump] = useState(0);
  const input = useRef<HTMLInputElement>(null);
  const box = useRef<HTMLDivElement>(null);
  useFocusTrap(box, open);

  useEffect(() => {
    const show = (m: "search" | "follow", type: FollowType | null, q0 = "", within0?: SearchScope) => {
      setMode(m); setOnlyType(type); setScope((type && TYPE_SCOPE[type]) || "all"); setWithin(within0 ?? "all");
      setQ(q0); setData(null); setActive(0); setOpen(true);
    };
    const onKey = (e: KeyboardEvent) => { if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") { e.preventDefault(); show("search", null); } };
    const onOpen = (e: Event) => {
      const d = (e as CustomEvent<{ mode?: "follow" | "search"; type?: FollowType; q?: string; scope?: SearchScope }>).detail ?? {};
      show(d.mode === "follow" ? "follow" : "search", d.type ?? null, d.q ?? "", d.scope);
    };
    addEventListener("keydown", onKey);
    addEventListener("palette:open", onOpen);
    return () => { removeEventListener("keydown", onKey); removeEventListener("palette:open", onOpen); };
  }, []);

  useEffect(() => {
    if (!open) return;
    input.current?.focus();
    box.current?.animate(prefersReducedMotion() ? [{ opacity: 0 }, { opacity: 1 }] : [{ opacity: 0, transform: "scale(0.98)" }, { opacity: 1, transform: "none" }],
      prefersReducedMotion() ? { duration: reducedFallback.duration } : { duration: duration.fast, easing: easing.enter });
  }, [open]);

  // Search as you type: a few per kind for "All", more for one kind. Stale responses are dropped.
  useEffect(() => {
    if (!open) return;
    const term = q.trim();
    if (term.length < 2) { setData(null); return; }
    const ctl = new AbortController();
    const t = setTimeout(() => {
      fetch(searchUrl(term, scope === "all" ? { scope: within, per: 5 } : { scope: within, kind: scope, limit: 8 }), { credentials: "include", signal: ctl.signal })
        .then((r) => (r.ok ? r.json() : null)).then((d: SearchResponse | null) => { if (d) { setData(d); setActive(0); } }).catch(() => {});
    }, 120);
    return () => { clearTimeout(t); ctl.abort(); };
  }, [q, scope, within, open]);

  const followed = (r: SearchResult) => OWN_FOLLOW.has(r.sub) && (!!r.followed || isSearchFollowed(r.follow, follows));
  const groups = useMemo(() => {
    const rs: PaletteItem[] = (data?.results ?? []).filter((r) => !onlyType || r.follow?.type === onlyType).map((r) => ({ ...r, followed: followed(r) }));
    return groupResults(rs.length ? [...rs, ...(mode === "search" ? actionsFor(q) : [])] : [], scope, scope === "all" ? 3 : 8);
  }, [data, q, scope, mode, onlyType, follows]); // eslint-disable-line react-hooks/exhaustive-deps
  const flat = groups.flatMap((g) => g.items);
  const current = flat[active];

  if (!open) return null;
  const close = () => setOpen(false);
  const follow = (r?: PaletteItem) => {
    if (!r) return;
    const f = r.kind === "action" ? { type: "topic" as const, keyword: r.q.toLowerCase(), label: r.q.charAt(0).toUpperCase() + r.q.slice(1) } : r.follow;
    if (!f) return;
    void followFromSearch(f).then((done) => { bump((n) => n + 1); if (done && mode === "follow") close(); });
  };
  const openResult = (r: PaletteItem | undefined, newTab = false) => {
    if (!r) return;
    if (r.kind === "action" || mode === "follow") { follow(r); return; }
    const href = hrefOf(r);
    if (!href) return;
    if (newTab) { open_(href); return; }
    close(); navigate(href);
  };
  const open_ = (href: string) => window.open(href, "_blank", "noopener");

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); close(); }
    else if (e.key === "ArrowDown") { e.preventDefault(); setActive((i) => Math.min(flat.length - 1, i + 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((i) => Math.max(0, i - 1)); }
    else if (e.key === "Enter" && e.shiftKey) { e.preventDefault(); follow(current); }
    else if (e.key === "Enter") { e.preventDefault(); openResult(current, e.metaKey || e.ctrlKey); }
    else if (e.key === "Tab") { e.preventDefault(); e.stopPropagation(); setScope((s) => nextScope(s, e.shiftKey)); }
  };
  const term = q.trim();
  const total = data?.counts.all ?? 0;

  return (
    <div className="sp-scrim" onMouseDown={(e) => { if (e.target === e.currentTarget) close(); }}>
      <div ref={box} className="sp" role="dialog" aria-modal="true" aria-label="Search" onKeyDown={onKeyDown}>
        <div className="sp-input">
          <Search size={20} aria-hidden />
          <input ref={input} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search meetings, bills, people, organizations" aria-label="Search"
            role="combobox" aria-expanded={flat.length > 0} aria-controls="sp-list" aria-autocomplete="list" aria-activedescendant={current ? `sp-${active}` : undefined} />
          <kbd>esc</kbd>
        </div>
        <div className="sp-scopes" role="tablist" aria-label="Scope">
          {SCOPES.map((s) => (
            <button key={s.id} role="tab" tabIndex={-1} aria-selected={scope === s.id} onClick={() => { setScope(s.id); input.current?.focus(); }}>{s.label}</button>
          ))}
        </div>
        {groups.length > 0 && (
          <ul className="sp-list" id="sp-list" role="listbox" aria-label="Results">
            {groups.map((g) => (
              <li key={g.label} role="presentation">
                <span className="sp-group" aria-hidden>{g.label}</span>
                <ul role="group" aria-label={g.label}>
                  {g.items.map((r) => {
                    const i = flat.indexOf(r);
                    const t = r.kind === "action" ? { title: r.title, meta: r.meta, metaHl: false } : paletteText(r, r.personId ? data?.people[r.personId] : undefined);
                    return (
                      <ListRow key={`${r.kind}:${r.id}`} id={`sp-${i}`} role="option" className="sp-row" wrap={false}
                        aria-selected={i === active} onMouseMove={() => i !== active && setActive(i)} onMouseDown={(e: React.MouseEvent) => { e.preventDefault(); openResult(r, e.metaKey || e.ctrlKey); }}>
                        <RowLead><RowIcon r={r} data={data} /></RowLead>
                        <RowMain min={80} className="sp-text">
                          <RowTitle className="sp-title">{t.title}</RowTitle>
                          {t.meta && <RowSub className="sp-meta">{t.metaHl ? <Hl text={t.meta} q={q} hl={r.kind !== "action" ? r.hl : undefined} /> : t.meta}</RowSub>}
                        </RowMain>
                        <RowTrail>
                          {r.kind !== "action" && r.followed && <span className="sp-following"><Check size={12} aria-hidden />Following</span>}
                          <kbd className="sp-enter" aria-hidden><CornerDownLeft size={12} /></kbd>
                        </RowTrail>
                      </ListRow>
                    );
                  })}
                </ul>
              </li>
            ))}
          </ul>
        )}
        {data && !groups.length && term.length > 1 && (
          <div className="sp-empty"><NoResults query={term} onBroaden={within === "follows" ? () => setWithin("all") : undefined} /></div>
        )}
        <footer className="sp-foot">
          <span><kbd>↑↓</kbd>to move</span>
          <span><kbd><CornerDownLeft size={11} /></kbd>{mode === "follow" ? "to follow" : opensAtMoment(current) ? "open at moment" : "to open"}</span>
          {mode === "search" && <span><kbd>⌘<CornerDownLeft size={11} /></kbd>new tab</span>}
          <span><kbd>tab</kbd>switch scope</span>
          {mode === "search" && term.length > 1 && total > 0 && (
            <a className="sp-all" href={`/dashboard/search?q=${encodeURIComponent(term)}`} onClick={close}>See all {total} results<ArrowRight size={13} aria-hidden /></a>
          )}
        </footer>
      </div>
    </div>
  );
}
